import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, ChevronLeft, ChevronRight, AlertCircle, Building2, Landmark, Filter, RotateCcw,
  Save, Trash2, Undo2, Check, CalendarCheck, FileText, CircleCheck, CircleDashed, Plane, Ban,
  Loader2, X, Menu, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import RequestLeaveDialog from '../components/RequestLeaveDialog';
import QuickBookPopover from '../components/QuickBookPopover';
import CancelLeaveDialog from '../components/CancelLeaveDialog';
import TimedDayFill from '../components/TimedDayFill';
import { DayMark, DayNumberOver } from '../components/DayMark';
import BrandedToaster from '../components/BrandedToaster';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, leaveApi, holidaysApi, departmentsApi, usersApi, locationsApi, blackoutsApi, settingsApi } from '../lib/api';
import { formatLeaveShort } from '../lib/leaveUnits';
import { leaveTypeIconComponent } from '../lib/leaveTypeIcons';
import { iconOn, numberOn } from '../lib/leaveColour';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameMonth, isSameDay, setMonth, setYear, parseISO } from 'date-fns';
import DateRangeField from '../components/DateRangeField';
import { NoLeaveDatesYours, NoLeaveDateActions, mine } from '../components/NoLeaveDatesYours';
// ⚠️ Pure, and CHECKED — `node src/lib/workingDays.checks.mjs`. Replaces date-fns
// `isWeekend`, which is Saturday and Sunday for everybody on earth.
import { isNonWorkingDay } from '../lib/workingDays';
import { roleLabel, roleBadgeClass } from '../lib/roleBadges';
import { bookingLength, withTimes, backAtWork, dayShare } from '../lib/bookingTime';

/*
 * THE TEAM CALENDAR — the wallchart on a page of its own (Warm Hive decision 9, Rob 2 + 2b A,
 * 28/10/2026; built 29/10/2026). It used to sit at the bottom of the dashboard inside
 * DashboardPage.js. This is a MOVE: the state, the filters, the saved view, quick book, the
 * hover cards, Cancel, "Book leave for …" and the No-leave dates box all came across
 * unchanged in what they do — tests/ui-inventory checks every control is still here.
 *
 * ⚠️ The words people read say "No-leave dates"; the code, the API (/blackouts) and the
 * database keep "blackout" (decision 10 — the same reasoning as Location/branch in CLAUDE.md).
 */

// Render any ISO date (YYYY-MM-DD) inside free text as UK DD/MM/YYYY
const ukDates = (str) => (str || '').replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) => `${d}/${m}/${y}`);

// ONE icon map for the whole app (lib/leaveTypeIcons) — the per-page copies
// only knew ~15 of the Settings picker's ~50 icons (Rob, 16 Jul).
const getLeaveTypeIcon = (iconName, className = "w-4 h-4 text-white") => {
  const IconComponent = leaveTypeIconComponent(iconName);
  return <IconComponent className={className} />;
};

// Component for rendering leave block with half-day support
const LeaveBlockCell = ({ leaveBlock, className = "", onClick, dataTestId, title }) => {
  const dayPortion = leaveBlock?.day_portion || 'full';
  // Navy on a pale leave colour, white otherwise (Rob's 4A, 29/10/2026 — lib/leaveColour).
  const icon = getLeaveTypeIcon(leaveBlock?.icon, `w-4 h-4 ${iconOn(leaveBlock?.color)}`);
  const isPending = leaveBlock?.status === 'pending';
  // The date, shown in place of the icon while the pointer is on the day (Rob, 03/11/2026).
  const n = leaveBlock?.date ? Number(leaveBlock.date.slice(8, 10)) : null;
  
  // ⚠️ A TIMED booking (10:00-12:00) is part of a day, not a day: a band from the bottom as
  // tall as the share of their day, with a clock (C16, Rob chose C, 23/10/2026). Before
  // this a 2-hour appointment filled the cell exactly like a full day.
  const share = dayShare(leaveBlock);
  if (share !== null) {
    return (
      <div
        className={`w-full h-full min-h-[26px] rounded-md relative overflow-hidden ${className}`}
        onClick={onClick}
        data-testid={dataTestId}
        data-timed="true"
        title={title}
      >
        <TimedDayFill color={leaveBlock.color} share={share} pending={isPending} n={n} />
      </div>
    );
  }

  // Waiting for approval: the day is drawn faded, but the date shown while the pointer is on it
  // stays full strength (Rob 2A, 03/11/2026) — so the fading is on an inner layer, not the cell.
  if (dayPortion === 'full' && isPending) {
    return (
      <div
        className={`w-full h-full min-h-[26px] rounded-md text-[10px] relative font-medium ${className}`}
        onClick={onClick}
        data-testid={dataTestId}
        title={title}
      >
        <div
          className="absolute inset-0 rounded-md flex items-center justify-center opacity-[.55] border-2 border-dashed"
          style={{ backgroundColor: leaveBlock.color, borderColor: leaveBlock.color }}
        >
          <span className="hh-day-icon">{icon}</span>
        </div>
        {n !== null && <DayNumberOver n={n} numClass="text-foreground" />}
      </div>
    );
  }

  // For full day, render normally - fill entire cell
  if (dayPortion === 'full') {
    return (
      <div 
        className={`w-full h-full min-h-[26px] rounded-md text-[10px] flex items-center justify-center font-medium ${
          isPending ? 'opacity-[.55] border-2 border-dashed' : ''
        } ${className}`}
        style={{ 
          backgroundColor: leaveBlock.color,
          borderColor: isPending ? leaveBlock.color : 'transparent'
        }}
        onClick={onClick}
        data-testid={dataTestId}
        title={title}
      >
        {n !== null ? <DayMark n={n} numClass={numberOn(leaveBlock.color)}>{icon}</DayMark> : icon}
      </div>
    );
  }
  
  // For half day, use diagonal clip-path
  // Morning (AM) = top-left triangle, Afternoon (PM) = bottom-right triangle
  const isMorning = dayPortion === 'morning';
  
  return (
    <div 
      className={`w-full h-full min-h-[26px] rounded-md relative overflow-hidden ${className}`}
      style={{ backgroundColor: leaveBlock.color + '26' }}
      onClick={onClick}
      data-testid={dataTestId}
      title={title}
    >
      {/* The colored diagonal portion */}
      <div 
        className={`absolute inset-0 flex items-center justify-center ${
          isPending ? 'opacity-[.55]' : ''
        }`}
        style={{ 
          backgroundColor: leaveBlock.color,
          clipPath: isMorning 
            ? 'polygon(0 0, 100% 0, 0 100%)' // Top-left triangle for morning
            : 'polygon(100% 0, 100% 100%, 0 100%)' // Bottom-right triangle for afternoon
        }}
      />
      {/* Dashed border for pending */}
      {isPending && (
        <div 
          className="absolute inset-0 border-2 border-dashed rounded-md"
          style={{ borderColor: leaveBlock.color }}
        />
      )}
      {/* Icon centered - positioned in the colored area */}
      <div 
        className={`hh-day-icon absolute flex items-center justify-center ${
          isMorning ? 'top-0.5 left-0.5' : 'bottom-0.5 right-0.5'
        }`}
        style={{ 
          width: '20px', 
          height: '20px',
        }}
      >
        {getLeaveTypeIcon(leaveBlock?.icon, `w-3 h-3 ${iconOn(leaveBlock?.color)}`)}
      </div>
      {n !== null && <DayNumberOver n={n} numClass="text-foreground hh-day-num-halo" />}
    </div>
  );
};

/**
 * A person's tags on their row ("First aider", "Keyholder") — Rob's 7B, 29/10/2026.
 * ⚠️ The SERVER decides who gets them (Rob's 8A: the person, admins and their manager — the
 * same people who see the allowance badge). Tags drive approvals and limits and a company
 * can name one anything, so other rows simply arrive without `tags`. This only draws.
 * Module level on purpose: a component declared inside another is a new type every render.
 */
const RowTags = ({ tags }) => {
  // A line of its own under the badges, on EVERY row — empty when there are no tags — so all
  // rows are three lines and every day box is the same size (Rob's B, 29/10/2026). Each tag by
  // name; past two, the rest become "+N". Hover the line for them all.
  const list = Array.isArray(tags) ? tags : [];
  const shown = list.slice(0, 2);
  const more = list.length - shown.length;
  return (
    <div
      className="hidden sm:flex flex-nowrap items-center gap-1 mt-0.5 h-4 overflow-hidden"
      title={list.length ? `Tags: ${list.map((t) => t.name).join(', ')}` : undefined}
      data-testid={list.length ? 'wallchart-row-tags' : undefined}
    >
      {shown.map((t) => (
        <span
          key={t.id || t.name}
          className="inline-flex items-center gap-1 text-[10px] leading-4 font-bold text-muted-foreground bg-secondary px-1.5 rounded-full whitespace-nowrap min-w-0 max-w-[100px]"
        >
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color || '#64748b' }} />
          <span className="truncate">{t.name}</span>
        </span>
      ))}
      {more > 0 && (
        <span className="text-[10px] leading-4 font-bold text-muted-foreground bg-secondary px-1.5 rounded-full whitespace-nowrap">+{more}</span>
      )}
    </div>
  );
};

const TeamCalendarPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0); // For refreshing sidebar badge
  const [wallchart, setWallchart] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const firstLoadRef = useRef(true);  // only the first load shows the full collapse spinner
  const [loading, setLoading] = useState(true);
  const [blackouts, setBlackouts] = useState([]);  // Leave blackout periods
  
  // Legend data - leave types that have any bookings (past, present, or future)
  const [leaveTypesInUse, setLeaveTypesInUse] = useState(new Map()); // Map of leave_type_name -> color
  const [hasBirthdaysInCalendar, setHasBirthdaysInCalendar] = useState(false);
  const [hasBankHolidaysInCalendar, setHasBankHolidaysInCalendar] = useState(false);
  const [hasPendingRequests, setHasPendingRequests] = useState(false);
  
  // Wallchart filter states
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsEnabled, setLocationsEnabled] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  // Find someone by name (Rob, 02/11/2026: "if a company has 100 staff, finding the person they
  // want could be a pain"). Names only (2A); NOT part of the saved view (3A) — it's for now.
  const [nameSearch, setNameSearch] = useState('');
  const [sortBy, setSortBy] = useState('default'); // 'default', 'first_name', 'last_name'
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Date picker temp selections (don't apply until "Go" is clicked)
  const [tempMonth, setTempMonth] = useState(null);
  const [tempYear, setTempYear] = useState(null);
  
  // Default view saved settings
  const [hasDefaultView, setHasDefaultView] = useState(false);
  const [isUsingDefaultView, setIsUsingDefaultView] = useState(true);
  
  // Leave details popover state
  const [openLeavePopover, setOpenLeavePopover] = useState(null); // Format: "userId-date"
  
  // Request Leave dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [quickBookDate, setQuickBookDate] = useState(null);
  const [quickBookLeaveTypeId, setQuickBookLeaveTypeId] = useState(null);
  
  // Blackout dialog state
  const [blackoutDialogOpen, setBlackoutDialogOpen] = useState(false);
  // Rob 1C + 2A (02/11/2026): the box has an "Add new" and a "Yours" tab, and Change re-opens the form
  // on one you added. Which ones are yours AND changeable, the server says (`can_change`).
  const [blackoutTab, setBlackoutTab] = useState('add');
  const [editingBlackout, setEditingBlackout] = useState(null);
  const [blackoutSaving, setBlackoutSaving] = useState(false);
  const [canCreateBlackouts, setCanCreateBlackouts] = useState(false);
  // What the No-leave dates box may offer — from GET /blackouts/options (the save's own rule).
  const [blackoutWholeCompany, setBlackoutWholeCompany] = useState(false);
  const [blackoutDepartments, setBlackoutDepartments] = useState([]);
  const [blackoutPeople, setBlackoutPeople] = useState([]);
  const [canBookTeamLeave, setCanBookTeamLeave] = useState(false);
  const [canCancelTeamLeave, setCanCancelTeamLeave] = useState(false);
  const [bookForUser, setBookForUser] = useState(null);
  const [blackoutForm, setBlackoutForm] = useState({
    name: '',
    reason: '',
    start_date: '',
    end_date: '',
    scope: '',  // Empty = "Choose option..."
    department_id: '',
    user_ids: [],  // Changed to array for multiple employees
    is_recurring: false
  });
  
  // Track "today" for the today indicator - normalize to midnight for date comparisons
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Get user-specific localStorage key for saved view
  const getSavedViewKey = () => `wallchart_default_view_${user?.id || 'unknown'}`;
  
  // Load default view from localStorage on mount (user-specific)
  useEffect(() => {
    if (!user?.id) return; // Wait for user to be loaded
    
    const savedView = localStorage.getItem(getSavedViewKey());
    if (savedView) {
      try {
        const parsed = JSON.parse(savedView);
        setHasDefaultView(true);
        setIsUsingDefaultView(true);
        setSelectedDepartment(parsed.department || 'all');
        setSelectedLocation(parsed.location || 'all');
        setSelectedManager(parsed.manager || 'all');
        setSelectedEmployee(parsed.employee || 'all');
        setSortBy(parsed.sortBy || 'default');
      } catch (e) {
        console.error('Failed to parse saved view', e);
      }
    } else {
      // No saved view for this user
      setHasDefaultView(false);
      setIsUsingDefaultView(false);
    }
  // `getSavedViewKey` is recreated each render; including it would rebuild this on every pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Redirect superadmins to their admin panel
    if (user?.role === 'superadmin') {
      navigate('/superadmin');
      return;
    }
    fetchData();
    fetchFilterData();
  // Runs once on mount and when the month changes. `fetchData` is recreated every render, so listing it would re-fetch endlessly. `navigate` is stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, user]);

  const fetchFilterData = async () => {
    try {
      const [deptsRes, usersRes, locationSettingsRes, locationsRes] = await Promise.all([
        departmentsApi.list().catch(() => ({ data: [] })),
        usersApi.list().catch(() => ({ data: [] })),
        locationsApi.getSettings().catch(() => ({ data: { multi_location_enabled: false } })),
        locationsApi.list().catch(() => ({ data: [] }))
      ]);
      setDepartments(deptsRes.data || []);
      setAllUsers(usersRes.data || []);
      setLocationsEnabled(locationSettingsRes.data.multi_location_enabled);
      setLocations(locationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  };

  // Handler for quick book - opens dialog with pre-filled date and leave type
  const handleQuickBook = (date, leaveType, entry) => {
    if (entry && entry.user_id !== (user?.id || user?.user_id)) {
      setBookForUser({ id: entry.user_id, name: entry.user_name });
    } else {
      setBookForUser(null);
    }
    setQuickBookDate(date);
    setQuickBookLeaveTypeId(leaveType.id);
    setRequestDialogOpen(true);
  };

  // The cancel box (DESIGN-part-cancel.md): the whole booking or just some of its days.
  // It names whose leave it is itself, so `name` is no longer needed here.
  const [cancelId, setCancelId] = useState(null);
  const handleCancelLeaveFor = (requestId) => {
    if (requestId) setCancelId(requestId);
  };

  const fetchData = async () => {
    if (firstLoadRef.current) setLoading(true);  // month changes refetch silently (no scroll jump)
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      
      // allSettled, NOT all: with Promise.all, ANY one of these seven failing blanked
      // the ENTIRE dashboard — wallchart, stats, approvals, the lot. (Found the hard
      // way: one 500 on /leave/allowances emptied the whole Team Wallchart.) Each
      // panel now degrades alone with an empty fallback.
      const settle = (fallback) => (r) => (r.status === 'fulfilled' ? r.value : (
        console.error('Team calendar fetch failed:', r.reason), { data: fallback }
      ));
      const [wallchartRes, holidaysRes, allRequestsRes, blackoutsRes] = (await Promise.allSettled([
        dashboardApi.getWallchart(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')),
        holidaysApi.list(new Date().getFullYear()),
        // Fetch all leave requests to build the legend (not filtered by date)
        leaveApi.listRequests({}),
        // Fetch active blackout periods
        blackoutsApi.list(false)
      ])).map((r, i) => settle([[], [], [], []][i])(r));

      setWallchart(wallchartRes.data);
      setBlackouts(blackoutsRes.data || []);
      
      const allHolidays = holidaysRes.data || [];
      
      // Build legend data from ALL leave requests (past, present, future)
      const allRequests = allRequestsRes.data || [];
      const typesMap = new Map();
      let hasPending = false;
      
      allRequests.forEach(req => {
        // Only include approved or pending requests (not cancelled/declined)
        if (req.status === 'approved' || req.status === 'pending') {
          if (req.leave_type_name && req.leave_type_color) {
            typesMap.set(req.leave_type_name, req.leave_type_color);
          }
          if (req.status === 'pending') {
            hasPending = true;
          }
        }
      });
      
      setLeaveTypesInUse(typesMap);
      setHasPendingRequests(hasPending);
      
      // Check for bank holidays (if any exist for the year)
      setHasBankHolidaysInCalendar(allHolidays.length > 0);
      
      // Check for birthdays in the wallchart data
      const hasBirthdays = wallchartRes.data?.some(entry => entry.birthday);
      setHasBirthdaysInCalendar(hasBirthdays);
      
      // Check if user can create blackouts (admin or manager with permission)
      const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
      if (isAdmin) {
        setCanBookTeamLeave(true);
        setCanCancelTeamLeave(true);
      } else if (user?.role === 'manager') {
        // Resolve manager permissions: per-user override, else tenant global default
        try {
          const userProfile = await usersApi.get(user.id || user.user_id);
          const userData = userProfile.data;
          if (userData?.use_global_manager_settings === false) {
            setCanBookTeamLeave(userData?.mgr_override_can_book_team_leave || false);
            setCanCancelTeamLeave(userData?.mgr_override_can_cancel_approved_leave || false);
          } else {
            const mgrSettings = await settingsApi.getManagerSettings();
            setCanBookTeamLeave(mgrSettings.data?.mgr_can_book_team_leave || false);
            setCanCancelTeamLeave(mgrSettings.data?.mgr_can_cancel_approved_leave || false);
          }
        } catch (e) {
          console.error('Error checking manager permissions:', e);
          setCanBookTeamLeave(false);
          setCanCancelTeamLeave(false);
        }
      }

      // The No-leave dates box: may this person add them, "Everyone", and exactly which departments
      // and people. ⚠️ The SERVER answers, with the save's own rule (GET /blackouts/options,
      // 02/11/2026) — it used to be worked out here from managed_department_ids + the manage scope,
      // which missed a manager whose reach is their own department. Once, on the first load.
      if (firstLoadRef.current && (isAdmin || user?.role === 'manager')) {
        const opt = await blackoutsApi.options().catch(() => ({ data: null }));
        setCanCreateBlackouts(!!(opt.data && opt.data.can_create));
        setBlackoutWholeCompany(!!(opt.data && opt.data.whole_company));
        setBlackoutDepartments((opt.data && opt.data.departments) || []);
        setBlackoutPeople((opt.data && opt.data.people) || []);
      }
      
    } catch (error) {
      console.error('Failed to fetch team calendar data:', error);
      toast.error('Failed to load the team calendar');
    } finally {
      setLoading(false);
      firstLoadRef.current = false;
    }
  };

  // On a phone the month doesn't fit, so the grid slides sideways (Rob's 5A). Open it on
  // today rather than on the 1st — only when it's this month and there's something to slide.
  const gridScrollRef = useRef(null);
  useEffect(() => {
    const box = gridScrollRef.current;
    if (!box || loading || box.scrollWidth <= box.clientWidth) return;
    const chip = box.querySelector('[data-testid="today-indicator"]');
    if (!chip) return;
    const cell = chip.parentElement.getBoundingClientRect();
    const frame = box.getBoundingClientRect();
    const pinned = box.querySelector('.sticky.left-0');
    const nameWidth = pinned ? pinned.getBoundingClientRect().width : 0;
    box.scrollLeft += cell.left - frame.left - nameWidth - cell.width;
  }, [loading, currentMonth]);

  // Refresh the calendar after any change (a booking, a cancellation)
  const refreshCalendar = () => {
    fetchData();
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Get unique managers from users
  const managers = useMemo(() => {
    return allUsers.filter(u => u.role === 'manager' || u.role === 'admin');
  }, [allUsers]);

  // Helper function to get role priority for sorting (lower = higher priority)
  const getRolePriority = (role) => {
    switch (role) {
      case 'admin': return 1;
      case 'manager': return 2;
      case 'employee': return 3;
      default: return 4;
    }
  };

  // Filter and sort wallchart data based on selections
  const filteredWallchart = useMemo(() => {
    let filtered = [...wallchart];
    
    // Location filter
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(entry => entry.location_id === selectedLocation);
    }
    
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(entry => entry.department_id === selectedDepartment);
    }
    
    // Manager filter - can be 'all', 'only_managers', or a specific manager ID
    if (selectedManager === 'only_managers') {
      filtered = filtered.filter(entry => entry.role === 'manager' || entry.role === 'admin');
    } else if (selectedManager !== 'all') {
      filtered = filtered.filter(entry => entry.manager_id === selectedManager);
    }
    
    // Employee filter - can be 'all', 'only_employees', or a specific employee ID
    if (selectedEmployee === 'only_employees') {
      filtered = filtered.filter(entry => entry.role === 'employee');
    } else if (selectedEmployee !== 'all') {
      filtered = filtered.filter(entry => entry.user_id === selectedEmployee);
    }
    
    // Find someone: any part of their first, last or full name (names only — Rob 2A, 02/11/2026).
    // `beforeSearch` is how many the other filters left, for the "2 of 40 people" count.
    const beforeSearch = filtered.length;
    const q = nameSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(entry => {
        const first = (entry.first_name || '').toLowerCase();
        const last = (entry.last_name || '').toLowerCase();
        return first.includes(q) || last.includes(q) || `${first} ${last}`.includes(q);
      });
    }

    // Sort the results
    const meId = user?.id || user?.user_id;
    filtered.sort((a, b) => {
      // Always show the logged-in user first
      if (a.user_id === meId && b.user_id !== meId) return -1;
      if (b.user_id === meId && a.user_id !== meId) return 1;
      // When filtered by department or manager, put managers/admins at top
      if (selectedDepartment !== 'all' || (selectedManager !== 'all' && selectedManager !== 'only_managers')) {
        const rolePriorityDiff = getRolePriority(a.role) - getRolePriority(b.role);
        if (rolePriorityDiff !== 0) return rolePriorityDiff;
      }
      
      // Apply user-selected sort
      if (sortBy === 'first_name') {
        return (a.first_name || '').localeCompare(b.first_name || '');
      } else if (sortBy === 'last_name') {
        return (a.last_name || '').localeCompare(b.last_name || '');
      }
      
      // Default: sort by role priority first, then by first name
      const roleDiff = getRolePriority(a.role) - getRolePriority(b.role);
      if (roleDiff !== 0) return roleDiff;
      return (a.first_name || '').localeCompare(b.first_name || '');
    });
    
    filtered.beforeSearch = beforeSearch;
    return filtered;
  }, [wallchart, selectedLocation, selectedDepartment, selectedManager, selectedEmployee, sortBy, user, nameSearch]);

  // Check if current month is today's month
  const isCurrentMonth = isSameMonth(currentMonth, today);

  // Helper function to check if a date is blacked out for a specific user
  const isBlackoutDate = (date, userDepartmentId, userId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateObj = new Date(dateStr);
    
    for (const blackout of blackouts) {
      const startDate = new Date(blackout.start_date);
      const endDate = new Date(blackout.end_date);
      
      // Check if date falls within blackout range
      if (dateObj >= startDate && dateObj <= endDate) {
        // Check scope
        if (blackout.scope === 'company') {
          return blackout;
        }
        if (blackout.scope === 'department' && blackout.department_id === userDepartmentId) {
          return blackout;
        }
        if (blackout.scope === 'user' && blackout.user_id === userId) {
          return blackout;
        }
      }
    }
    return null;
  };

  // Helper function to get all affected users for a blackout (aggregates same-name blackouts)
  const getBlackoutAffectedUsers = (blackout) => {
    if (blackout.scope !== 'user') return null;
    
    // Find all blackouts with the same name and date range
    const relatedBlackouts = blackouts.filter(b => 
      b.scope === 'user' && 
      b.name === blackout.name && 
      b.start_date === blackout.start_date && 
      b.end_date === blackout.end_date
    );
    
    if (relatedBlackouts.length <= 1) return blackout.user_name;
    
    // Return comma-separated list of names
    return relatedBlackouts
      .map(b => b.user_name)
      .filter(Boolean)
      .join(', ');
  };

  // Reset filters handler
  const handleResetFilters = () => {
    setSelectedLocation('all');
    setSelectedDepartment('all');
    setSelectedManager('all');
    setSelectedEmployee('all');
    setSortBy('default');
    setNameSearch('');
    setIsUsingDefaultView(false);
  };

  // Save current filters as default view (user-specific)
  const handleSaveDefaultView = () => {
    const viewSettings = {
      department: selectedDepartment,
      location: selectedLocation,
      manager: selectedManager,
      employee: selectedEmployee,
      sortBy: sortBy
    };
    localStorage.setItem(getSavedViewKey(), JSON.stringify(viewSettings));
    setHasDefaultView(true);
    setIsUsingDefaultView(true);
    toast.success('Default view saved');
  };

  // Delete default view (user-specific)
  const handleDeleteDefaultView = () => {
    localStorage.removeItem(getSavedViewKey());
    setHasDefaultView(false);
    setIsUsingDefaultView(false);
    toast.success('Default view removed');
  };

  // Restore default view (user-specific)
  const handleRestoreDefaultView = () => {
    const savedView = localStorage.getItem(getSavedViewKey());
    if (savedView) {
      try {
        const parsed = JSON.parse(savedView);
        setSelectedDepartment(parsed.department || 'all');
        setSelectedLocation(parsed.location || 'all');
        setSelectedManager(parsed.manager || 'all');
        setSelectedEmployee(parsed.employee || 'all');
        setSortBy(parsed.sortBy || 'default');
        setIsUsingDefaultView(true);
      } catch (e) {
        console.error('Failed to restore saved view', e);
      }
    }
  };

  // Handle creating a blackout
  const meId = user?.id || user?.user_id;
  const refreshBlackouts = async () => {
    const res = await blackoutsApi.list(false).catch(() => ({ data: null }));
    if (res.data) setBlackouts(res.data);
  };
  const openChangeBlackout = (b) => {
    setEditingBlackout(b);
    setBlackoutForm({
      name: b.name || '',
      reason: b.reason || '',
      start_date: b.start_date,
      end_date: b.end_date,
      scope: b.scope,
      department_id: b.department_id || '',
      user_ids: b.user_id ? [b.user_id] : [],
      is_recurring: !!b.is_recurring,
    });
    setBlackoutTab('add');
    setBlackoutDialogOpen(true);
  };
  const closeBlackoutBox = () => {
    setBlackoutDialogOpen(false);
    setBlackoutTab('add');
    if (editingBlackout) {
      setEditingBlackout(null);
      setBlackoutForm({ name: '', reason: '', start_date: '', end_date: '', scope: '', department_id: '', user_ids: [], is_recurring: false });
    }
  };

  const handleSaveBlackout = async () => {
    if (!blackoutForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!blackoutForm.start_date || !blackoutForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (!blackoutForm.scope) {
      toast.error('Please select who this applies to');
      return;
    }
    if (blackoutForm.scope === 'department' && !blackoutForm.department_id) {
      toast.error('Please select a department');
      return;
    }
    if (blackoutForm.scope === 'user' && blackoutForm.user_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    setBlackoutSaving(true);
    try {
      if (editingBlackout) {
        // Changing one you added: the name, reason, dates and "every year" — not who it's for.
        await blackoutsApi.update(editingBlackout.id, {
          name: blackoutForm.name,
          reason: blackoutForm.reason || null,
          start_date: blackoutForm.start_date,
          end_date: blackoutForm.end_date,
          is_recurring: blackoutForm.is_recurring,
        });
        toast.success('No-leave dates changed');
        setEditingBlackout(null);
      // If scope is 'user' and multiple employees selected, create one blackout per employee
      } else if (blackoutForm.scope === 'user' && blackoutForm.user_ids.length > 0) {
        for (const userId of blackoutForm.user_ids) {
          const data = {
            name: blackoutForm.name,
            reason: blackoutForm.reason || null,
            start_date: blackoutForm.start_date,
            end_date: blackoutForm.end_date,
            scope: 'user',
            department_id: null,
            user_id: userId,
            is_recurring: blackoutForm.is_recurring
          };
          await blackoutsApi.create(data);
        }
        toast.success(`No-leave dates added for ${blackoutForm.user_ids.length} employee(s)`);
      } else {
        const data = {
          name: blackoutForm.name,
          reason: blackoutForm.reason || null,
          start_date: blackoutForm.start_date,
          end_date: blackoutForm.end_date,
          scope: blackoutForm.scope,
          department_id: blackoutForm.scope === 'department' ? blackoutForm.department_id : null,
          user_id: null,
          is_recurring: blackoutForm.is_recurring
        };
        await blackoutsApi.create(data);
        toast.success('No-leave dates added');
      }
      
      setBlackoutDialogOpen(false);
      setBlackoutForm({
        name: '',
        reason: '',
        start_date: '',
        end_date: '',
        scope: '',
        department_id: '',
        user_ids: [],
        is_recurring: false
      });
      
      // Refresh blackouts
      const res = await blackoutsApi.list(false);
      setBlackouts(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add the no-leave dates');
    } finally {
      setBlackoutSaving(false);
    }
  };

  // Jump to today handler
  const handleReturnToToday = () => {
    setCurrentMonth(new Date());
  };

  // Month selector for the popover
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 5; y++) {
    years.push(y);
  }

  // Initialize temp selections when popover opens
  const handleDatePickerOpen = (open) => {
    if (open) {
      setTempMonth(currentMonth.getMonth());
      setTempYear(currentMonth.getFullYear());
    }
    setDatePickerOpen(open);
  };

  // Apply the selected month/year
  const handleApplyMonthYear = () => {
    if (tempMonth !== null && tempYear !== null) {
      let newDate = setMonth(new Date(), tempMonth);
      newDate = setYear(newDate, tempYear);
      setCurrentMonth(newDate);
    }
    setDatePickerOpen(false);
  };

  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;


  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} refreshTrigger={sidebarRefreshTrigger} />

      <main className="flex-1 overflow-auto">
        <header className="relative z-30">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-4 sm:px-6 lg:px-8 pt-6 pb-1">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                <h1 className="font-heading text-2xl sm:text-[30px] sm:leading-tight font-semibold">Team calendar</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Who&rsquo;s off, and when</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canCreateBlackouts && (
                <Button 
                  variant="outline"
                  className="bg-card text-foreground hover:bg-secondary"
                  onClick={() => setBlackoutDialogOpen(true)}
                  data-testid="block-leave-btn"
                >
                  <Ban className="w-4 h-4 mr-2 text-hive-danger-fg" />
                  No-leave dates
                </Button>
              )}
              <Button 
                className="btn-primary" 
                data-testid="request-leave-btn"
                onClick={() => setRequestDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Request leave
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:px-8 space-y-5">
          <div className="card-hive p-6" data-testid="wallchart">
            {/* Wallchart Header with Title and Controls */}
            <div className="flex flex-col gap-4 mb-4">
              {/* Top row: the month (left), My team and the Filtered chip (right). The page's
                  own title says "Team calendar", so the card no longer repeats one. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  {/* Return to Today button - shown when not on current month */}
                  {!isCurrentMonth && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleReturnToToday}
                      className="text-primary border-primary/50 hover:bg-primary/10"
                      data-testid="return-to-today-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Today
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="rounded-full bg-background dark:bg-secondary hover:bg-secondary"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    data-testid="prev-month-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {/* Clickable Month/Year with Popover */}
                  <Popover open={datePickerOpen} onOpenChange={handleDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="font-heading text-base font-semibold min-w-[150px] hover:bg-secondary"
                        data-testid="month-year-picker-btn"
                      >
                        {format(currentMonth, 'MMMM yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4" align="center">
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-center text-muted-foreground">Jump to month</div>
                        <div className="grid grid-cols-3 gap-2">
                          {months.map((month, index) => (
                            <Button
                              key={month}
                              variant={tempMonth === index ? "default" : "ghost"}
                              size="sm"
                              className="text-xs"
                              onClick={() => setTempMonth(index)}
                            >
                              {month.substring(0, 3)}
                            </Button>
                          ))}
                        </div>
                        <div className="border-t pt-4">
                          <div className="text-sm font-medium text-center text-muted-foreground mb-2">Year</div>
                          <div className="grid grid-cols-4 gap-2">
                            {years.map((year) => (
                              <Button
                                key={year}
                                variant={tempYear === year ? "default" : "ghost"}
                                size="sm"
                                className="text-xs"
                                onClick={() => setTempYear(year)}
                              >
                                {year}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <div className="border-t pt-4 flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {tempMonth !== null && tempYear !== null ? `${months[tempMonth]} ${tempYear}` : 'Select month & year'}
                          </span>
                          <Button 
                            size="sm" 
                            onClick={handleApplyMonthYear}
                            disabled={tempMonth === null || tempYear === null}
                            data-testid="date-picker-go-btn"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Go
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="rounded-full bg-background dark:bg-secondary hover:bg-secondary"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    data-testid="next-month-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {/* My Team quick filter - shown only for managers (not admins who see everyone) */}
                  {isManager && !isAdmin && (
                    <Button
                      variant={selectedManager === user?.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (selectedManager === user?.id) {
                          // Toggle off - reset to show all
                          setSelectedManager('all');
                          setIsUsingDefaultView(false);
                        } else {
                          // Filter to my team
                          setSelectedManager(user?.id);
                          setSelectedDepartment('all');
                          setSelectedEmployee('all');
                          setIsUsingDefaultView(false);
                        }
                      }}
                      className={selectedManager === user?.id 
                        ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                        : "text-primary border-primary/50 hover:bg-primary/10"}
                      data-testid="my-team-filter-btn"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      My team
                    </Button>
                  )}
                  {/* Filtered indicator - shown when any filter is active */}
                  {(selectedLocation !== 'all' || selectedDepartment !== 'all' || selectedManager !== 'all' || selectedEmployee !== 'all') && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-hive-pending-bg text-hive-pending-text dark:bg-secondary dark:text-hive-orange" data-testid="filtered-indicator">
                      <Filter className="w-3 h-3" />
                      Filtered
                    </span>
                  )}
                </div>
              </div>
              
              {/* Filter Controls Row */}
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-hive-line-soft bg-hive-tile dark:bg-secondary dark:border-border px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground mr-1">
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                </div>

                {/* Find someone by name (Rob 1A–4A, 02/11/2026) — narrows the rows as you type. */}
                <label className="flex items-center gap-2 h-8 px-3 rounded-full bg-card border border-input w-full sm:w-[145px] 2xl:w-[175px] min-[1700px]:w-[220px] focus-within:ring-1 focus-within:ring-ring">
                  <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <input
                    type="text"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setNameSearch(''); }}
                    placeholder="Find someone…"
                    aria-label="Find someone by name"
                    className="flex-1 min-w-0 bg-transparent outline-none text-[13px] font-bold placeholder:font-semibold placeholder:text-muted-foreground"
                    data-testid="filter-name-search"
                  />
                  {nameSearch && (
                    <button type="button" onClick={() => setNameSearch('')} aria-label="Clear the search"
                      className="text-muted-foreground hover:text-foreground" data-testid="filter-name-search-clear">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </label>
                {nameSearch.trim() && (
                  <span className="text-[12.5px] font-bold text-muted-foreground" data-testid="filter-name-search-count">
                    {filteredWallchart.length} of {filteredWallchart.beforeSearch} {filteredWallchart.beforeSearch === 1 ? 'person' : 'people'}
                  </span>
                )}
                
                {/* Below 1536 px the drop-downs and the search are a little narrower, so the whole row stays on
                    ONE line on a 1366 laptop too (Rob A, 02/11/2026 — measured at 1366, 1536 and 1920). */}
                {/* Department Filter */}
                {departments.length > 0 && (
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-[140px] 2xl:w-[170px] h-8 text-[13px] font-bold rounded-full bg-card" data-testid="filter-department">
                    <SelectValue placeholder="All departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
                
                {/* Location Filter - only when multi-location is enabled */}
                {locationsEnabled && locations.length > 0 && (
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="w-[140px] 2xl:w-[170px] h-8 text-[13px] font-bold rounded-full bg-card" data-testid="filter-location">
                      <SelectValue placeholder="All locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All locations</SelectItem>
                      {locations.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Manager Filter */}
                {managers.length > 0 && (
                <Select value={selectedManager} onValueChange={setSelectedManager}>
                  <SelectTrigger className="w-[140px] 2xl:w-[170px] h-8 text-[13px] font-bold rounded-full bg-card" data-testid="filter-manager">
                    <SelectValue placeholder="All managers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All managers</SelectItem>
                    <SelectItem value="only_managers">Only managers</SelectItem>
                    {managers.map(mgr => (
                      <SelectItem key={mgr.id} value={mgr.id}>
                        {mgr.first_name} {mgr.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
                
                {/* Employee Filter */}
                {allUsers.length > 0 && (
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-[150px] 2xl:w-[190px] h-8 text-[13px] font-bold rounded-full bg-card" data-testid="filter-employee">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All employees</SelectItem>
                    <SelectItem value="only_employees">Only employees (no managers)</SelectItem>
                    {allUsers.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
                
                {/* Sort By Filter */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[130px] 2xl:w-[150px] h-8 text-[13px] font-bold rounded-full bg-card" data-testid="filter-sort">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default sort</SelectItem>
                    <SelectItem value="first_name">First name</SelectItem>
                    <SelectItem value="last_name">Last name</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Separator */}
                <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
                
                {/* Default View Controls */}
                <div className="flex items-center gap-1">
                  {/* Save as Default View button */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleSaveDefaultView}
                    className="text-hive-burnt dark:text-hive-orange font-bold hover:text-hive-burnt-hover"
                    title="Save current filters as default view"
                    data-testid="save-default-view-btn"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Save view
                  </Button>
                  
                  {/* Show Restore/Delete buttons only if a default view exists */}
                  {hasDefaultView && (
                    <>
                      {/* Restore Default View - show when not currently using default */}
                      {!isUsingDefaultView && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={handleRestoreDefaultView}
                          className="text-primary hover:text-primary/80"
                          title="Restore your saved default view"
                          data-testid="restore-default-view-btn"
                        >
                          <Undo2 className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      )}
                      
                      {/* Delete Default View */}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleDeleteDefaultView}
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete your saved default view"
                        data-testid="delete-default-view-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
                
                {/* Reset Filters Button - shown when any filter is active */}
                {(selectedLocation !== 'all' || selectedDepartment !== 'all' || selectedManager !== 'all' || selectedEmployee !== 'all' || sortBy !== 'default' || nameSearch.trim()) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleResetFilters}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="reset-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Wallchart Grid */}
            {/* The people rows scroll INSIDE the card (as tall as the screen allows, never under 480 px), with the dates (sticky top) and
                the names (sticky left) staying in view — the handoff's Team Wallchart. The
                cells stretch so a whole month fits a laptop (Rob, mock v5). */}
            <div className="mt-4 rounded-2xl border border-border overflow-hidden">
            {/* 12 people at once on a 24" 1080p screen, with the legend still in view; fewer on a
                smaller screen (Rob, 29/10/2026). The height rule is .hh-wallchart-box in index.css,
                because it has to allow for the strip across the top (--hh-dock). */}
            <div className="overflow-auto hh-wallchart-box" ref={gridScrollRef}>
              <div className="min-w-[760px]">
                {/* Header row - dates with bank holiday indicators and today marker */}
                <div className="flex border-b border-border sticky top-0 z-20 bg-card">
                  <div className="w-28 sm:w-52 xl:w-60 flex-shrink-0 px-2 sm:px-3 py-[3px] flex items-center text-[13px] font-bold text-muted-foreground sticky left-0 z-30 bg-card border-r border-border">Employee</div>
                  {days.map((day) => {
                    // ⚠️ THE HEADER IS DELIBERATELY NEUTRAL — Rob's option A, 07/08/2026.
                    //
                    // It used to shade Saturday and Sunday, and tint a column purple if
                    // ANY holiday in the company fell on it. With offices in six
                    // countries that meant purple columns for Algerian and Moldovan days
                    // that applied to almost nobody, and weekend stripes that were wrong
                    // for the Algiers rows underneath.
                    //
                    // ⚠️ NOTHING IS LOST: every row already marks its OWN non-working
                    // days and its OWN bank holidays. A header can only ever show one
                    // company's week, and there is no longer only one.
                    const isToday = isSameDay(day, today);
                    return (
                      <div 
                        key={day.toISOString()} 
                        className="flex-1 min-w-[22px] px-0.5 py-1.5 text-center relative"
                        title={isToday ? 'Today' : ''}
                      >
                        {/* Today: a navy chip on the date (the handoff), where the bouncing arrow was. */}
                        <div
                          className={`rounded-lg py-0.5 leading-tight ${isToday ? 'bg-primary text-primary-foreground' : ''}`}
                          data-testid={isToday ? 'today-indicator' : undefined}
                        >
                          <div className={`text-[13px] font-bold ${isToday ? '' : 'text-foreground'}`}>{format(day, 'd')}</div>
                          {/* Two letters until the screen is wide enough for three (they'd collide). */}
                          <div className={`text-[10.5px] font-semibold ${isToday ? 'opacity-90' : 'text-muted-foreground'}`}>
                            <span className="2xl:hidden">{format(day, 'EEEEEE')}</span>
                            <span className="hidden 2xl:inline">{format(day, 'EEE')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* ⚠️ THE COMPANY-WIDE BANK HOLIDAY ROW HAS BEEN REMOVED — step 4,
                    07/08/2026, and it is the thing the design doc named:
                    "the wallchart marks bank holidays in a header row from the
                    company-wide list, so in a two-country company everyone sees both
                    countries' days."

                    It drew one purple band across the whole grid for every holiday any
                    office observed. With Rob's six countries that was Algerian, Moldovan
                    and Saudi days stamped across British employees who do not get them.

                    ⚠️ NOTHING IS LOST. Each employee row already marks its OWN bank
                    holidays from `entry.bank_holiday_dates`, which is resolved per person
                    by country AND region. A single row can only ever be right for one
                    country, and there is no longer only one. */}

                {/* User rows */}
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-muted-foreground">Loading the team…</span>
                    </div>
                  </div>
                ) : filteredWallchart.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {wallchart.length === 0 
                      ? 'No employees found. Add your team to see the team calendar.'
                      : nameSearch.trim()
                        ? `No one matches “${nameSearch.trim()}”${filteredWallchart.beforeSearch < wallchart.length ? ' with these filters' : ''}.`
                        : 'No employees match the selected filters.'}
                  </div>
                ) : (
                  filteredWallchart.map((entry) => {
                    // Role badge: one colour set for the whole app (lib/roleBadges, C34).
                    const roleBadge = { label: roleLabel(entry.role), className: roleBadgeClass(entry.role) };
                    
                    // Check if the logged-in user can view this employee's year planner
                    // User can click if: they are Admin, OR they are this employee's manager, OR it's their own row
                    const canViewUserLeave = isAdmin || user?.id === entry.user_id || entry.can_view_details === true || user?.id === entry.manager_id;
                    
                    return (
                    <div key={entry.user_id} className="group flex border-b border-hive-line-softer dark:border-border/50 last:border-b-0">
                      {canViewUserLeave ? (
                        <div 
                          className="w-28 sm:w-52 xl:w-60 flex-shrink-0 px-2 sm:px-3 py-[3px] flex items-center gap-2.5 cursor-pointer transition-colors sticky left-0 z-10 bg-card group-hover:bg-secondary border-r border-border"
                          onClick={() => navigate(`/leave/user/${entry.user_id}`)}
                          title={`View ${entry.user_name}'s year planner`}
                          data-testid={`wallchart-user-${entry.user_id}`}
                        >
                          {entry.profile_picture_url ? (
                            <img src={entry.profile_picture_url} alt={entry.user_name} className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-hive-orange to-hive-annual flex items-center justify-center text-white text-[10px] sm:text-[12px] font-bold flex-shrink-0">
                              {entry.user_name.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            {/* "Name · Dept" on one line, then the badges (the handoff). */}
                            <p className="text-[13.5px] font-bold truncate group-hover:text-hive-burnt dark:group-hover:text-hive-orange">
                              {/* First name only on a phone, where the pinned column is narrow (Rob's 5A). */}
                              <span className="sm:hidden">{entry.first_name || entry.user_name}</span>
                              <span className="hidden sm:inline">{entry.user_name}</span>
                              {entry.department_name && (
                                <span className="hidden sm:inline font-semibold text-muted-foreground"> · {entry.department_name}</span>
                              )}
                            </p>
                            <div className="hidden sm:flex flex-nowrap items-center gap-1 mt-0.5 overflow-hidden">
                              <span className={`text-[10px] leading-4 px-1.5 rounded-full font-extrabold whitespace-nowrap ${roleBadge.className}`}>
                                {roleBadge.label}
                              </span>
                              {(user?.id === entry.user_id || isAdmin || user?.id === entry.manager_id) && entry.allowance_remaining !== null && (
                                <>
                                  {/* `allowance_remaining` is ALREADY net of bank holidays
                                      (D8 — deducted from the pot server-side, once).
                                      Subtracting them again here would double-count. */}
                                  <span className="text-[10px] leading-4 font-extrabold text-white bg-hive-navy dark:text-hive-navy dark:bg-primary px-1.5 rounded-full" title="Leave remaining">
                                    {formatLeaveShort(entry.allowance_remaining, { unit: entry.display_unit, minutes: entry.allowance_remaining_minutes })}
                                  </span>
                                  {!entry.works_public_holidays && entry.bank_holidays_remaining > 0 && (
                                    <span className="text-[10px] leading-4 font-extrabold text-purple-700 bg-hive-bh-bg dark:text-purple-300 dark:bg-purple-500/20 px-1.5 rounded-full" title="Bank holidays remaining">
                                      +{entry.bank_holidays_remaining}bh
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {/* Line 3: tags — on every row, empty or not, so every row (and so
                                every day box) is the same height (Rob's B, 29/10/2026). */}
                            <RowTags tags={entry.tags} />
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="w-28 sm:w-52 xl:w-60 flex-shrink-0 px-2 sm:px-3 py-[3px] flex items-center gap-2.5 sticky left-0 z-10 bg-card group-hover:bg-secondary border-r border-border"
                          data-testid={`wallchart-user-${entry.user_id}`}
                        >
                          {entry.profile_picture_url ? (
                            <img src={entry.profile_picture_url} alt={entry.user_name} className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-secondary flex items-center justify-center text-foreground text-[10px] sm:text-[12px] font-bold flex-shrink-0">
                              {entry.user_name.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-bold truncate">
                              <span className="sm:hidden">{entry.first_name || entry.user_name}</span>
                              <span className="hidden sm:inline">{entry.user_name}</span>
                              {entry.department_name && (
                                <span className="hidden sm:inline font-semibold text-muted-foreground"> · {entry.department_name}</span>
                              )}
                            </p>
                            <div className="hidden sm:flex flex-nowrap items-center gap-1 mt-0.5 overflow-hidden">
                              <span className={`text-[10px] leading-4 px-1.5 rounded-full font-extrabold whitespace-nowrap ${roleBadge.className}`}>
                                {roleBadge.label}
                              </span>
                            </div>
                            {/* Tags only ever arrive for rows the viewer may see them on — the
                                server decides (Rob's 8A); this just draws what came. */}
                            <RowTags tags={entry.tags} />
                          </div>
                        </div>
                      )}
                      {days.map((day) => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        // ⚠️ THIS ROW'S OWN WEEK. `entry.working_days` is resolved
                        // server-side by the same chain that PRICES their leave — their
                        // dated pattern, else their location, else the company. If the
                        // grid drew a different week from the one it charges, the screen
                        // and the balance would disagree and only one gets checked.
                        const offToday = isNonWorkingDay(day, entry.working_days);
                        const leaveBlock = entry.leave_blocks.find(b => b.date === dateStr);
                        // Return-to-work date = next working day (skip weekends) after the leave ends
                        // ⚠️ A TIMED booking is back the SAME day at its end time (C16).
                        const back = leaveBlock ? backAtWork(leaveBlock, entry.working_days) : null;
                        const returnDate = back ? back.date : null;
                        const bankHoliday = (entry.bank_holiday_dates || []).find(h => h.date === dateStr);
                        // Show bank holiday if employee doesn't work on public holidays (region-aware)
                        // ⚠️ A bank holiday landing on THEIR day off isn't shown — but for an Algiers
                        // employee that is Friday, not Sunday. Reading Saturday/Sunday here hid
                        // holidays that genuinely fell on their working weekend.
                        const showBankHoliday = bankHoliday && !entry.works_public_holidays && !offToday;
                        // Check if it's this employee's birthday
                        const isBirthday = entry.birthday === dateStr;
                        // Check if today for column highlight
                        const isToday = isSameDay(day, today);
                        // Check if user can view leave details (same as row click permission)
                        const canViewLeaveDetails = canViewUserLeave;
                        // Check if this date is blacked out for this user
                        const blackout = isBlackoutDate(day, entry.department_id, entry.user_id);
                        const isBlackedOut = !!blackout;
                        
                        return (
                          <div 
                            key={day.toISOString()} 
                            className={`hh-day flex-1 min-w-[22px] p-[2px] relative bg-clip-content ${isToday ? 'shadow-[inset_2px_0_0_hsl(var(--primary)),inset_-2px_0_0_hsl(var(--primary))]' : 'rounded-lg'} ${offToday ? 'bg-hive-weekend dark:bg-background/80' : 'bg-hive-tile dark:bg-secondary/60'} ${isBirthday ? 'ring-2 ring-inset ring-rose-400 z-[1]' : ''}`}
                          >
                            {/* Blackout indicator */}
                            {isBlackedOut && !leaveBlock && !showBankHoliday && !offToday && (
                              <HoverCard openDelay={200} closeDelay={100}>
                                <HoverCardTrigger asChild>
                                  <div 
                                    className="w-full h-full min-h-[26px] rounded-md flex items-center justify-center cursor-help"
                                    style={{ 
                                      background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 4px, rgba(239,68,68,0.25) 4px, rgba(239,68,68,0.25) 8px)',
                                      border: '1px solid rgba(239,68,68,0.3)'
                                    }}
                                    data-testid={`blackout-${entry.user_id}-${dateStr}`}
                                  >
                                    <DayMark n={day.getDate()} numClass="text-hive-danger-fg dark:text-red-300"><Ban className="w-4 h-4 text-red-400" /></DayMark>
                                  </div>
                                </HoverCardTrigger>
                                <HoverCardContent className="w-64 p-0 rounded-xl overflow-hidden shadow-xl" align="start">
                                  <div className="px-3.5 py-2.5 border-b border-border bg-hive-danger-bg dark:bg-red-500/15">
                                    <div className="flex items-center gap-2">
                                      <Ban className="w-4 h-4 text-hive-danger-fg dark:text-red-300" />
                                      <span className="font-heading font-semibold text-sm text-hive-danger-fg dark:text-red-300">No-leave dates</span>
                                    </div>
                                  </div>
                                  <div className="p-3 space-y-2">
                                    <p className="font-medium text-sm">{blackout.name}</p>
                                    {blackout.reason && (
                                      <p className="text-xs text-muted-foreground">{blackout.reason}</p>
                                    )}
                                    <div className="text-xs">
                                      <span className="text-muted-foreground">Applies to: </span>
                                      <span className="font-medium">
                                        {blackout.scope === 'company' && 'Everyone (Company-wide)'}
                                        {blackout.scope === 'department' && (blackout.department_name || 'Department')}
                                        {blackout.scope === 'user' && (getBlackoutAffectedUsers(blackout) || 'Specific Employee(s)')}
                                      </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(blackout.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                      {' - '}
                                      {new Date(blackout.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                    <p className="text-xs font-bold text-hive-danger-fg dark:text-red-300">Leave can&rsquo;t be booked on this date</p>
                                    <NoLeaveDateActions blackout={blackout} meId={meId} onChange={openChangeBlackout} onRemoved={refreshBlackouts} />
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                            )}
                            {/* Birthday badge stays visible even when on leave that day */}
                            {isBirthday && leaveBlock && (
                              <div className="hh-day-icon absolute inset-0 z-20 pointer-events-none" title={`${entry.user_name}'s Birthday 🎉`}>
                                {/* Gift-wrap: gold satin ribbon cross filling the cell + a big bow on top */}
                                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[10px]" style={{ background: 'linear-gradient(90deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[10px]" style={{ background: 'linear-gradient(0deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[24px] leading-none drop-shadow z-30">🎀</span>
                              </div>
                            )}
                            {leaveBlock ? (
                              canViewLeaveDetails ? (
                                // Authorized user - show hover card with details
                                <HoverCard openDelay={200} closeDelay={100}>
                                <HoverCardTrigger asChild>
                                  <div className="w-full h-full cursor-pointer hover:ring-2 hover:ring-white/30 transition-all rounded">
                                    <LeaveBlockCell 
                                      leaveBlock={leaveBlock}
                                      dataTestId={`leave-block-${entry.user_id}-${dateStr}`}
                                    />
                                  </div>
                                </HoverCardTrigger>
                                <HoverCardContent className="w-72 p-0 rounded-xl overflow-hidden shadow-xl" align="start">
                                  <div className="p-3 border-b border-border" style={{ backgroundColor: leaveBlock.color + '20' }}>
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold text-sm">{leaveBlock.leave_type}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        leaveBlock.status === 'approved' 
                                          ? 'bg-hive-success-bg text-hive-success-fg dark:bg-emerald-500/20 dark:text-emerald-300 font-bold' 
                                          : 'bg-hive-pending-bg text-hive-pending-text dark:bg-amber-500/20 dark:text-hive-orange-light font-bold'
                                      }`}>
                                        {leaveBlock.status === 'approved' ? 'Approved' : 'Pending'}
                                      </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{entry.user_name}</p>
                                    {leaveBlock.limit_exception && (
                                      <p className="text-xs text-red-600 font-bold mt-1 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" /> Exception — {ukDates(leaveBlock.limit_exception_note) || 'exceeds a leave limit'}
                                      </p>
                                    )}
                                  </div>
                                  <div className="p-3 space-y-3">
                                    {/* Birthday callout */}
                                    {isBirthday && (
                                      <div className="flex items-center gap-2 rounded-md bg-pink-500/10 border border-pink-500/30 px-2.5 py-2">
                                        <span className="text-lg">🎂</span>
                                        <p className="text-sm font-semibold text-pink-600">It&rsquo;s {entry.user_name.split(' ')[0]}&rsquo;s birthday!</p>
                                      </div>
                                    )}
                                    {/* Department */}
                                    {entry.department_name && (
                                      <div className="flex items-start gap-2">
                                        <Building2 className="w-4 h-4 text-muted-foreground mt-0.5" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">Department</p>
                                          <p className="text-sm">{entry.department_name}</p>
                                        </div>
                                      </div>
                                    )}
                                    {/* Dates */}
                                    <div className="flex items-start gap-2">
                                      <CalendarCheck className="w-4 h-4 text-muted-foreground mt-0.5" />
                                      <div>
                                        <p className="text-xs text-muted-foreground">Dates</p>
                                        <p className="text-sm">
                                          {leaveBlock.start_date && leaveBlock.end_date ? (
                                            leaveBlock.start_date === leaveBlock.end_date
                                              ? withTimes(format(parseISO(leaveBlock.start_date), 'EEE dd MMM yyyy'), leaveBlock)
                                              : `${format(parseISO(leaveBlock.start_date), 'EEE dd MMM')} - ${format(parseISO(leaveBlock.end_date), 'EEE dd MMM yyyy')}`
                                          ) : dateStr}
                                          {!!leaveBlock.total_days && (
                                            <span className="text-muted-foreground ml-1">({bookingLength(leaveBlock)})</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    {/* Return to work */}
                                    {returnDate && (
                                      <div className="flex items-start gap-2">
                                        <Plane className="w-4 h-4 text-muted-foreground mt-0.5" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">Back at work</p>
                                          <p className="text-sm">{format(returnDate, 'EEE dd MMM yyyy')}{back.time ? `, from ${back.time}` : ''}</p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Reason */}
                                    {leaveBlock.reason && (
                                      <div className="flex items-start gap-2">
                                        <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">Reason</p>
                                          <p className="text-sm">{leaveBlock.reason}</p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Approver - only for approved requests */}
                                    {leaveBlock.status === 'approved' && leaveBlock.approver_name && (
                                      <div className="flex items-start gap-2">
                                        <CircleCheck className="w-4 h-4 text-green-500 mt-0.5" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">Approved by</p>
                                          {leaveBlock.approval_chain && leaveBlock.approval_chain.length > 1 ? (
                                            <div className="text-sm space-y-0.5">
                                              {leaveBlock.approval_chain
                                                .filter(step => step.status === 'APPROVED' && step.approver_name)
                                                .map((step, idx) => (
                                                  <div key={idx} className="flex items-center gap-1">
                                                    <span className="text-green-500">✓</span>
                                                    <span>{step.approver_name}</span>
                                                    <span className="text-xs text-muted-foreground">({step.level_name || `L${step.level_order}`})</span>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          ) : (
                                            <p className="text-sm">
                                              {leaveBlock.approver_name}
                                              {leaveBlock.approved_at && (
                                                <span className="text-muted-foreground text-xs ml-1">
                                                  on {format(parseISO(leaveBlock.approved_at), 'dd MMM yyyy')}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Pending indicator with approval progress */}
                                    {leaveBlock.status === 'pending' && (
                                      <div className="flex items-start gap-2">
                                        <CircleDashed className="w-4 h-4 text-hive-burnt dark:text-hive-orange mt-0.5" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">Status</p>
                                          {leaveBlock.approval_chain && leaveBlock.total_levels > 1 ? (
                                            <div className="space-y-0.5">
                                              <p className="text-sm font-bold text-hive-pending-text dark:text-hive-orange-light">
                                                Level {leaveBlock.current_level}/{leaveBlock.total_levels}
                                              </p>
                                              {leaveBlock.approval_chain.map((step, idx) => (
                                                <div key={idx} className="text-xs flex items-center gap-1">
                                                  <span className={step.status === 'APPROVED' ? 'text-green-500' : step.status === 'PENDING' ? 'text-hive-burnt dark:text-hive-orange' : 'text-red-500'}>
                                                    {step.status === 'APPROVED' ? '✓' : step.status === 'PENDING' ? '⏳' : '✗'}
                                                  </span>
                                                  <span className="text-muted-foreground">{step.level_name || `Level ${step.level_order}`}</span>
                                                  {step.approver_name && <span>- {step.approver_name}</span>}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-sm font-bold text-hive-pending-text dark:text-hive-orange-light">Awaiting approval</p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* Cancel action for admins / permitted managers / the owner */}
                                    {leaveBlock.request_id && leaveBlock.status !== 'cancelled' && (() => {
                                      const myId = user?.id || user?.user_id;
                                      const canCancel = entry.user_id === myId || isAdmin || (canCancelTeamLeave && entry.can_manage === true);
                                      if (!canCancel) return null;
                                      return (
                                        <button
                                          onClick={() => handleCancelLeaveFor(leaveBlock.request_id, entry.user_name)}
                                          className="w-full mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-500/10 rounded-md py-1.5 transition-colors"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                          Cancel this leave
                                        </button>
                                      );
                                    })()}
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                              ) : (
                                // Unauthorized user - show leave block without hover card (no details)
                                <LeaveBlockCell 
                                  leaveBlock={leaveBlock}
                                  title={`${entry.user_name} - ${leaveBlock.leave_type}`}
                                  dataTestId={`leave-block-${entry.user_id}-${dateStr}`}
                                />
                              )
                            ) : showBankHoliday ? (
                              <div 
                                className="w-full h-full min-h-[26px] rounded-md bg-purple-500/85 flex items-center justify-center"
                                title={`${bankHoliday.name} (Bank Holiday)`}
                              >
                                <DayMark n={day.getDate()} numClass="text-white"><Landmark className="w-4 h-4 text-white" /></DayMark>
                              </div>
                            ) : isBirthday ? (
                              (() => {
                                const myId = user?.id || user?.user_id;
                                const isOwnRow = entry.user_id === myId;
                                const canBookOther = !isOwnRow && (isAdmin || (canBookTeamLeave && entry.can_manage === true));
                                // ⚠️ NOT SHADING — THIS DECIDES WHAT CAN BE BOOKED, and
                                // it was the real bug hiding inside step 4. Asking
                                // `isWeekend` meant an employee in Algiers could NOT book
                                // their working Sunday and COULD book their non-working
                                // Friday. Their own week now decides.
                                const canBookHere = (isOwnRow && !offToday && day >= today && !isBlackedOut) || (canBookOther && !offToday);
                                const box = 'linear-gradient(135deg,#ef7a7a,#dc5f5f)';
                                const ribbon = (
                                  <>
                                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[10px]" style={{ background: 'linear-gradient(90deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                                    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[10px]" style={{ background: 'linear-gradient(0deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                                    <span className="hh-day-icon absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[24px] leading-none drop-shadow">🎀</span>
                                    <DayNumberOver n={day.getDate()} numClass="text-white hh-day-num-dark-halo" />
                                  </>
                                );
                                if (!canBookHere) return (
                                  <div className="relative w-full h-full min-h-[26px] rounded-md overflow-hidden" style={{ background: box }} title={`${entry.user_name}'s Birthday 🎉`}>
                                    {ribbon}
                                  </div>
                                );
                                return (
                                  <QuickBookPopover date={day} onSelectLeaveType={(d, lt) => handleQuickBook(d, lt, entry)}>
                                    <button
                                      className="relative w-full h-full min-h-[26px] rounded-md overflow-hidden hover:ring-2 hover:ring-white/70 transition-all"
                                      style={{ background: box }}
                                      title={isOwnRow ? 'Your birthday \u2014 click to book leave' : `Book leave for ${entry.user_name}`}
                                    >
                                      {ribbon}
                                    </button>
                                  </QuickBookPopover>
                                );
                              })()
                            ) : (
                              // Empty cell - quick book. Own future row, or admin/permitted-manager booking for a team member (any date)
                              (() => {
                                const myId = user?.id || user?.user_id;
                                const isOwnRow = entry.user_id === myId;
                                const canBookOther = !isOwnRow && (isAdmin || (canBookTeamLeave && entry.can_manage === true));
                                const showOwn = isOwnRow && !offToday && day >= today && !isBlackedOut;
                                const showOther = canBookOther && !offToday;
                                if (!showOwn && !showOther) return null;
                                return (
                                <QuickBookPopover date={day} onSelectLeaveType={(d, lt) => handleQuickBook(d, lt, entry)}>
                                  <button 
                                    className="w-full h-full min-h-[26px] rounded-md hover:bg-primary/15 transition-colors flex items-center justify-center"
                                    title={isOwnRow ? 'Click to book leave' : `Book leave for ${entry.user_name}`}
                                  >
                                    <Plus className="w-3 h-3 text-muted-foreground opacity-0 hover:opacity-100" />
                                  </button>
                                </QuickBookPopover>
                                );
                              })()
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                  })
                )}
              </div>
            </div>
            </div>

            {/* Legend - Shows leave types that have bookings anywhere in the calendar (past, present, future) */}
            {(hasBirthdaysInCalendar || hasBankHolidaysInCalendar || leaveTypesInUse.size > 0 || hasPendingRequests || blackouts.length > 0) && (
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-3 border-t border-border text-[12.5px] text-muted-foreground">
                {hasBirthdaysInCalendar && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🎁</span>
                    <span>Birthday</span>
                  </div>
                )}
                {hasBankHolidaysInCalendar && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-purple-500" />
                    <span>Bank holiday</span>
                  </div>
                )}
                {blackouts.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ 
                        background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 2px, rgba(239,68,68,0.3) 2px, rgba(239,68,68,0.3) 4px)',
                        border: '1px solid rgba(239,68,68,0.4)'
                      }}
                    />
                    <span>No-leave dates</span>
                  </div>
                )}
                {/* Dynamic leave types from all bookings */}
                {Array.from(leaveTypesInUse.entries()).map(([typeName, color]) => (
                  <div key={typeName} className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                    <span>{typeName}</span>
                  </div>
                ))}
                {hasPendingRequests && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded border-2 border-dashed border-muted-foreground opacity-60" />
                    <span>Pending</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* The cancel box — the whole booking or just some of its days (DESIGN-part-cancel.md). */}
      <CancelLeaveDialog requestId={cancelId} onClose={() => setCancelId(null)} onDone={refreshCalendar} />

      {/* Request Leave Dialog */}
      <RequestLeaveDialog 
        open={requestDialogOpen} 
        onOpenChange={(open) => {
          setRequestDialogOpen(open);
          if (!open) {
            setQuickBookDate(null);
            setQuickBookLeaveTypeId(null);
            setBookForUser(null);
          }
        }}
        onSuccess={refreshCalendar}
        initialDate={quickBookDate}
        initialLeaveTypeId={quickBookLeaveTypeId}
        bookForUserId={bookForUser?.id}
        bookForUserName={bookForUser?.name}
        allowPastDates={!!bookForUser}
      />
      
      {/* The No-leave dates box (was "Block Leave"; decision 10, Rob 1A). Code names stay blackout. */}
      {blackoutDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              {editingBlackout ? 'Change no-leave dates' : 'No-leave dates'}
            </h3>
            {!editingBlackout && (
              <div className="inline-flex gap-1 p-1 rounded-full bg-hive-line-soft dark:bg-secondary mb-4" role="tablist">
                {[['add', 'Add new'], ['yours', `Yours (${mine(blackouts, meId).length})`]].map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={blackoutTab === k}
                    onClick={() => setBlackoutTab(k)} data-testid={`no-leave-tab-${k}`}
                    className={`px-3.5 py-1.5 rounded-full text-[13px] font-bold transition-colors ${blackoutTab === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {blackoutTab === 'yours' && !editingBlackout ? (
              <>
                <NoLeaveDatesYours blackouts={blackouts} meId={meId} onChange={openChangeBlackout} onRemoved={refreshBlackouts} />
                <div className="flex justify-end mt-6">
                  <Button variant="secondary" onClick={closeBlackoutBox}>Close</Button>
                </div>
              </>
            ) : (<>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={blackoutForm.name}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, name: e.target.value })}
                  placeholder="e.g., Christmas Shutdown, Month-End Close"
                  className="input-hive mt-1"
                />
              </div>
              
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={blackoutForm.reason}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, reason: e.target.value })}
                  placeholder="e.g., All staff required for stocktake"
                  className="input-hive mt-1"
                />
              </div>
              
              {/* ONE pop-up for both days (DateRangeField, Rob 3A 02/11/2026). The form keeps its
                  yyyy-mm-dd strings; the field works in days. */}
              <DateRangeField
                startLabel="Start date *"
                endLabel="End date *"
                start={blackoutForm.start_date ? parseISO(blackoutForm.start_date) : null}
                end={blackoutForm.end_date ? parseISO(blackoutForm.end_date) : null}
                onChange={({ start, end }) => setBlackoutForm({ ...blackoutForm, start_date: start ? format(start, 'yyyy-MM-dd') : '', end_date: end ? format(end, 'yyyy-MM-dd') : '' })}
              />
              
              {editingBlackout ? (
                <p className="text-sm"><span className="text-muted-foreground">For: </span><span className="font-semibold">
                  {editingBlackout.scope === 'company' ? 'Everyone' : editingBlackout.scope === 'department' ? (editingBlackout.department_name || 'A department') : (editingBlackout.user_name || 'One person')}
                </span></p>
              ) : (<>
              <div>
                <Label>Applies To</Label>
                <select
                  value={blackoutForm.scope}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, scope: e.target.value, user_ids: [] })}
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Choose option...</option>
                  {blackoutWholeCompany && (
                    <option value="company">Everyone (Company-wide)</option>
                  )}
                  <option value="department">Specific Department</option>
                  <option value="user">Specific Employee(s)</option>
                </select>
              </div>
              
              {blackoutForm.scope === 'department' && (
                <div>
                  <Label>Department *</Label>
                  <select
                    value={blackoutForm.department_id}
                    onChange={(e) => setBlackoutForm({ ...blackoutForm, department_id: e.target.value })}
                    className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select department...</option>
                    {/* Already only the departments this person may block (the server's list) */}
                    {blackoutDepartments
                      .map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}
              
              {blackoutForm.scope === 'user' && (
                <div>
                  <Label>Employee(s) * <span className="text-xs text-muted-foreground font-normal">(select one or more)</span></Label>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-input rounded-md bg-background">
                    {/* Already only the people this person may block (the server's list) */}
                    {blackoutPeople
                      .map(u => (
                        <label 
                          key={u.id} 
                          className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/50 cursor-pointer border-b border-border last:border-0"
                        >
                          <input
                            type="checkbox"
                            checked={blackoutForm.user_ids.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBlackoutForm({ ...blackoutForm, user_ids: [...blackoutForm.user_ids, u.id] });
                              } else {
                                setBlackoutForm({ ...blackoutForm, user_ids: blackoutForm.user_ids.filter(id => id !== u.id) });
                              }
                            }}
                            className="w-4 h-4 rounded border-input accent-primary"
                          />
                          <span className="text-sm">{u.first_name} {u.last_name}</span>
                        </label>
                      ))
                    }
                  </div>
                  {blackoutForm.user_ids.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {blackoutForm.user_ids.length} employee(s) selected
                    </p>
                  )}
                </div>
              )}
              </>)}
              
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                <input
                  type="checkbox"
                  checked={blackoutForm.is_recurring}
                  onChange={(e) => setBlackoutForm({ ...blackoutForm, is_recurring: e.target.checked })}
                  className="w-5 h-5 rounded border-input accent-primary"
                />
                <div>
                  <p className="font-medium">Recurring annually</p>
                  <p className="text-xs text-muted-foreground">These dates will repeat every year</p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="secondary" 
                className="flex-1"
                onClick={closeBlackoutBox}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white" 
                onClick={handleSaveBlackout}
                disabled={blackoutSaving}
              >
                {blackoutSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                {editingBlackout ? 'Save changes' : 'Add no-leave dates'}
              </Button>
            </div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCalendarPage;
